import { useIntl } from 'react-intl';

import { getPageTitle } from '@/utils/labelUtils';

import type { TypographyProps } from '@/components/Typography';
import { theme } from '@/theme';

import * as S from './SectionHeader.styles';

export interface SectionHeaderProps extends Pick<
  TypographyProps,
  'textAlign' | 'className'
> {
  className?: string;
  color?: string;
  colorized?: boolean;
  disableGutters?: boolean;
  htmlElementTag?: keyof HTMLElementTagNameMap;
  numberOfColoredWords?: number | null | undefined;
  sizeDesktop?: number;
  sizeMobile?: number;
  text?: string;
}

export const SectionHeader = ({
  color = theme.colors.secondary,
  colorized = true,
  disableGutters,
  htmlElementTag = 'h3',
  sizeDesktop = 40,
  sizeMobile = 34,
  text = '',
  ...props
}: SectionHeaderProps) => {
  const { formatMessage } = useIntl();
  const message = getPageTitle(text);
  const translatedText = message ? formatMessage(message) : text;

  const textLines = (colorized ? translatedText : text)
    ?.split('<br>')
    .map((line, lineIndex, arr) => {
      const elements = [
        <span
          key={`${line}-${lineIndex}`}
          data-testid={`SectionHeaderLine-${lineIndex}`}
        >
          {line}
        </span>,
      ];

      if (lineIndex < arr.length - 1) {
        elements.push(<br key={`br-${lineIndex}`} />);
      }

      return elements;
    });

  return (
    <S.SectionHeader
      $color={color}
      as={htmlElementTag}
      data-test-id='SectionHeader'
      disableGutters={disableGutters}
      sizeDesktop={sizeDesktop}
      sizeMobile={sizeMobile}
      {...props}
    >
      {textLines}
    </S.SectionHeader>
  );
};
